import { Box, Card, CardContent, Grid, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import CardComp from "shared/components/card";
import Chart from "shared/components/chart";
import { toastMessage } from "shared/components/toast";
import useBreadcrumbs from "shared/customHooks/useBreadCrumbs";
import { routeConstant } from "shared/routes/routesConstant";
import { getAnalyticsOverview } from "shared/services/productManagerService";
import styles from "./style.module.scss";

function AppAnalyticsOverview() {
  const [loading, setLoading] = useState<boolean>(false);
  const [dateRange, setDateRange] = useState<any>({
    startDate: moment().subtract(30, "days").toDate(),
    endDate: new Date(),
  });
  const [analyticsData, setAnalyticsData] = useState<any>(null);

  useBreadcrumbs([
    {
      label: routeConstant.dashboard.title,
      route: routeConstant.dashboard.path,
    },
    {
      label: routeConstant.appAnalytics.title,
      route: routeConstant.appAnalytics.path,
    },
    {
      label: routeConstant.appAnalyticsOverview.title,
      route: routeConstant.appAnalyticsOverview.path,
    },
  ]);

  const onChangeDate = (dates: any) => {
    const [start, end] = dates;
    setDateRange({
      startDate: start,
      endDate: end,
    });
  };

  const fetchOverviewData = () => {
    setLoading(true);
    const dateFrom = dateRange.startDate
      ? moment(dateRange.startDate).format("YYYY-MM-DD")
      : undefined;
    const dateTo = dateRange.endDate
      ? moment(dateRange.endDate).format("YYYY-MM-DD")
      : undefined;

    getAnalyticsOverview(dateFrom, dateTo)
      .then(({ data }) => {
        setAnalyticsData(data?.data);
        setLoading(false);
      })
      .catch((err) => {
        console.log("err", err?.response?.data?.message);
        toastMessage("error", err?.response?.data?.message);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (dateRange.endDate) {
      fetchOverviewData();
    }
  }, [dateRange]);

  const summaryCards = [
    {
      title: "Total Active Installs",
      value: analyticsData?.summary?.total_active_installs || 0,
    },
    {
      title: "Total Installs",
      value: analyticsData?.summary?.total_installs || 0,
    },
    {
      title: "Total Uninstalls",
      value: analyticsData?.summary?.total_uninstalls || 0,
    },
    {
      title: "Active Users (7 Days)",
      value: analyticsData?.summary?.active_users_7_days || 0,
    },
    {
      title: "Retention Rate",
      value: `${analyticsData?.summary?.retention_rate?.toFixed(2) || 0}%`,
    },
  ];

  const chartData = {
    labels: analyticsData?.install_trend?.map((item: any) => item.date) || [],
    datasets: [
      {
        label: "Installs",
        data: analyticsData?.install_trend?.map((item: any) => item.count) || [],
        borderColor: "rgb(75, 192, 192)",
        backgroundColor: "rgba(75, 192, 192, 0.2)",
        tension: 0.1,
      },
    ],
  };

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Typography variant="h4">App Analytics Overview</Typography>
        <Box className={styles.datePicker}>
          <DatePicker
            selected={dateRange.startDate}
            onChange={onChangeDate}
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            selectsRange
            dateFormat="dd/MM/yyyy"
            placeholderText="Select date range"
            className={styles.dateInput}
          />
        </Box>
      </Box>

      <Grid container spacing={3} className={styles.statsContainer}>
        {summaryCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={4} lg={2.4} key={index}>
            <CardComp
              title={card.title}
              stats={card.value}
              isLoading={loading}
            />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} className={styles.chartContainer}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Platform Breakdown
              </Typography>
              {analyticsData?.platform_breakdown && (
                <Box className={styles.platformStats}>
                  {analyticsData.platform_breakdown.map((item: any) => (
                    <Box key={item.platform} className={styles.platformItem}>
                      <Typography variant="body1">
                        {item.platform.toUpperCase()}
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {item.count}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Install Trend
              </Typography>
              {analyticsData?.install_trend && (
                <Chart
                  labels={chartData.labels}
                  sales={chartData.datasets[0].data}
                />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default AppAnalyticsOverview;
